import { merge } from 'lodash';
import { Container } from 'lucide-react';
import React from 'react';

import { Icon } from '@/components/icon';
import { FilterData } from '@/components/ui/data-table/filter-toolbar';
import { SHIPMENT_STATUS } from '@/lib/constants';
import {
  ShipmentFilterPort,
  ShipmentFilters,
  ShipmentJourneyTimelineItem,
  ShipmentModel,
  ShipmentTrackingDataModel,
} from '@/lib/models/shipment/types';
import { ShipmentJourneyModel, ShipmentJourneyModelSegment } from '@/lib/models/shipment-journey/types';
import { getShipmentCargo, getShipmentDelay } from '@/lib/transformers/shipment/utils';
import { formatDateStringToIso } from '@/utils/format/date-time';
import { handleUnknownPortCode } from '@/utils/ports';

/**
 * Transforms Cargoes Tracking Data to a Fluent ShipmentModel
 */
export const transformShipmentTrackingDataToShipmentModel = (
  trackingData: ShipmentTrackingDataModel,
  existingShipment: ShipmentModel,
): ShipmentModel => {
  const { portToPort, outsourcedLogisticsTransport } = trackingData.shipmentLegs;
  const { milestones, shipmentJourney, ...trackingDataWithoutExtraData } = trackingData;

  const parseValue = (value: string | null | undefined): string | null => {
    if (!value) return null;
    return value;
  };

  const currentEtaString = formatDateStringToIso(
    portToPort?.dischargePortEta || portToPort?.destinationPortEta || outsourcedLogisticsTransport?.eta,
  );
  const originalEtaString = existingShipment.originalEta || currentEtaString || undefined;
  const parcelNumber = existingShipment.parcelNumber || trackingData.logisticsId || undefined;
  const delay = getShipmentDelay(trackingData, existingShipment);

  const shipment: ShipmentModel = {
    ...existingShipment,
    containerNumber: existingShipment.containerNumber || trackingData.containerNumber,
    mblNumber: existingShipment.mblNumber || trackingData.mblNumber || undefined,
    bookingNumber: existingShipment.bookingNumber || trackingData.bookingNumber || undefined,
    parcelNumber,
    status: trackingData.status,
    subStatus: trackingData.subStatus2 || trackingData.subStatus1,
    originPortCode: parseValue(
      handleUnknownPortCode(
        portToPort?.loadingPortCode || portToPort?.originPortCode || outsourcedLogisticsTransport?.origin || '',
      ),
    ),
    originPortName: parseValue(
      portToPort?.loadingPort || portToPort?.originPort || outsourcedLogisticsTransport?.origin || '',
    ),
    destinationPortCode: parseValue(
      handleUnknownPortCode(
        portToPort?.dischargePortCode ||
          portToPort?.destinationPortCode ||
          outsourcedLogisticsTransport?.destination ||
          '',
      ),
    ),
    destinationPortName: parseValue(
      portToPort?.dischargePort || portToPort?.destinationPort || outsourcedLogisticsTransport?.destination || '',
    ),
    eta: currentEtaString,
    ata: formatDateStringToIso(
      portToPort?.dischargePortAta || portToPort?.destinationPortAta || outsourcedLogisticsTransport?.ata,
    ),
    etd: formatDateStringToIso(
      portToPort?.loadingPortEtd || portToPort?.originPortEtd || outsourcedLogisticsTransport?.etd,
    ),
    atd: formatDateStringToIso(
      portToPort?.loadingPortAtd || portToPort?.originPortAtd || outsourcedLogisticsTransport?.atd,
    ),
    delay,
    carrier: parcelNumber ? trackingData.logisticsPartner : parseValue(portToPort?.carrier),
    carrierId: parseValue(trackingData.carrierScac),
    trackingUpdatedAt: trackingData.trackingUpdatedAt,
    trackingData: trackingDataWithoutExtraData,
    externalId: trackingData.shipmentNumber,
    originalEta: originalEtaString,
    cargo: getShipmentCargo(trackingData),
    milestones,
    journey: mergeShipmentJourneyModel(existingShipment.journey, shipmentJourney),
  };

  return shipment;
};

const shipmentFilterTitleMap: Record<keyof ShipmentFilters, string> = {
  carrier: 'Carrier',
  destinationPortCode: 'Destination',
  originPortCode: 'Origin',
  status: 'Status',
  subStatus: 'Sub-status',
  type: 'Mode',
};

const labelMap: Record<keyof ShipmentFilters, Record<string, { label: string; icon?: React.ReactNode }>> = {
  type: {
    air: { label: 'Air Cargo', icon: <Icon name="plane" size="14px" color="black" /> },
    intermodal: { label: 'Intermodal', icon: <Container className="size-[14px] text-black" /> },
  },
  destinationPortCode: {},
  originPortCode: {},
  subStatus: {},
  status: {
    ACTIVE: { label: 'Active' },
    COMPLETED: { label: 'Completed' },
    NEW: { label: 'New' },
  },
  carrier: {},
};

export const transformShipmentApiFiltersToFilterData = (filters: ShipmentFilters): FilterData => {
  return Object.entries(filters).reduce((acc, [key, values]): FilterData => {
    if (!filters[key as keyof ShipmentFilters]) return acc;
    return {
      ...acc,
      [key]: [
        {
          title: shipmentFilterTitleMap[key as keyof ShipmentFilters],
          items: values
            // Filter out any values that are null
            .filter((value: string | ShipmentFilterPort) =>
              typeof value === 'string' ? value !== '' : Boolean(value?.code || value?.name),
            )
            .map((value: string | ShipmentFilterPort) => {
              if (typeof value === 'string') {
                const labelData = labelMap[key as keyof ShipmentFilters]?.[value] || { label: value };
                return { value, ...labelData };
              }
              return { value: value.code, label: [value.code, value.name].filter(Boolean).join(' - ') };
            }),
        },
      ],
    };
  }, {} as FilterData);
};

/**
 * Transforms a ShipmentJourney into JourneyTimelineItem
 */
export const transformShipmentJourneyToShipmentJourneyTimelineItem = (
  journey?: ShipmentJourneyModel,
): ShipmentJourneyTimelineItem[] | undefined => {
  if (!journey) return undefined;

  const { segments, ports } = journey;

  return segments.reduce((accum, segment, index) => {
    const isLast = index === journey.segments.length - 1;
    const originLocation = segment.locations[0];
    const destinationLocation = segment.locations[segment.locations.length - 1];
    const items = [...accum];

    const originItem: ShipmentJourneyTimelineItem = {
      status: segment.status,
      progressPercent: `${segment.progressPercent}%`,
      mode: segment.vehicle.type,
      ...originLocation,
      port: (originLocation.portId && ports[originLocation.portId]) || undefined,
    };

    items.push(originItem);

    if (isLast) {
      const destinationItem: ShipmentJourneyTimelineItem = {
        status: segment.status,
        progressPercent: `${segment.progressPercent}%`,
        mode: segment.vehicle.type,
        ...destinationLocation,
        port: (destinationLocation.portId && ports[destinationLocation.portId]) || undefined,
      };

      items.push(destinationItem);
    }

    return items;
  }, [] as ShipmentJourneyTimelineItem[]);
};

export const mergeShipmentJourneyModel = (
  prevJourney?: ShipmentJourneyModel,
  newJourney?: ShipmentJourneyModel,
): ShipmentJourneyModel | undefined => {
  if (!newJourney) return undefined;

  const { ports: prevPorts, segments: prevSegments } = prevJourney || {};
  const { ports: newPorts, segments: newSegments } = newJourney;

  const mergedSegments = newSegments.map((segmentB) => {
    const segmentA = prevSegments?.find((s) => s.key === segmentB.key);
    if (segmentA) {
      // If a previous segment exists, attempt to merge it whilst preserving vehicle data
      return mergeShipmentJourneyModelSegment(segmentA, segmentB);
    }
    return segmentB;
  });

  return {
    ports: merge({}, prevPorts, newPorts),
    segments: mergedSegments,
  };
};

export const mergeShipmentJourneyModelSegment = (
  segmentA: ShipmentJourneyModelSegment | undefined,
  segmentB: ShipmentJourneyModelSegment,
): ShipmentJourneyModelSegment => {
  if (!segmentA) return segmentB;
  if (!segmentB) return segmentA;

  return {
    ...segmentB,
    vehicle: segmentB.status === SHIPMENT_STATUS.COMPLETED ? segmentA.vehicle : segmentB.vehicle,
  };
};
